/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HealthScoreModule
 */

import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';

import {
    IOperationalStatus,
    OperationalState,
} from 'generated-types';

import {
    SignpostPosition,
    signpostPositionMap,
    SIGNPOST_LEFT_CONNECTED_POSITION,
    SIGNPOST_RIGHT_CONNECTED_POSITION,
} from 'ng/modules/tooltip/components/signpost/signpost.constants';

import { ConnectedPosition } from '@angular/cdk/overlay';
import { HealthScoreClassPipe } from 'ng/modules/health-score/pipes/health-score-class.pipe';
import { L10nService } from '@vmw/ngx-vip';
import { Pool } from 'object-types';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { Subject } from 'rxjs';
import { StringService } from 'ng/modules/core/services/string/string.service';
import { IParsedFault, parseFaults } from 'ng/modules/virtual-service/utils/virtual-service-faults.utils';
import { TVirtualServiceFaults } from 'ng/modules/virtual-service/store/virtual-service.state';

import * as globalL10n from 'global-l10n';
import * as l10n from './avi-health-score.l10n';

import { IRuntime } from '../../health-score.types';
import './avi-health-score.component.less';

/**
 * Set of operational statuses for deactivated icons.
 */
const deactivatedIconOperStates = new Set([
    OperationalState.OPER_DISABLED,
    OperationalState.OPER_INACTIVE,
    OperationalState.OPER_UNUSED,
]);

/**
 * Set of operational statuses for no exclamation icon.
 */
const noExclamationIconOperStates = new Set([
    OperationalState.OPER_UP,
    OperationalState.OPER_DISABLED,
    OperationalState.OPER_INACTIVE,
    OperationalState.OPER_UNUSED,
    OperationalState.OPER_ERROR_DISABLED,
    OperationalState.OPER_DISABLING,
]);

/**
 * Set of operational statuses when health score is available.
 */
const availableHealthScoreOperStates = new Set([
    OperationalState.OPER_UP,
    OperationalState.OPER_DISABLING,
    OperationalState.OPER_ERROR_DISABLED,
]);

/**
 * Health score views based on pool state.
 */
enum HealthScoreView {
    DOWN = 'down',
    UP = 'up',
    LOADING = 'loading',
    ERROR = 'error',
}

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;
const selector = 'avi-health-score';

/**
 * @description Avi health score component.
 * @author Deepak Chaudhary, Shubam Bindal
 */
@Component({
    selector,
    templateUrl: './avi-health-score.component.html',
    providers: [
        HealthScoreClassPipe,
    ],
})
export class AviHealthScoreComponent implements OnChanges, OnDestroy {
    /**
     * Runtime data for the given VS / Pool / Servers.
     * This data is used to render the health score.
     */
    @Input()
    public runTime: IRuntime;

    @Input()
    public limit: number;

    @Input()
    public step: number;

    @Input()
    public hideViewHealthButton: boolean;

    @Input()
    public objectName?: string;

    @Input()
    public isWafPolicyInEvaluationMode = false;

    @Output()
    public onViewHealthClick = new EventEmitter<void>();

    @Output()
    public onHealthScoreIconClick = new EventEmitter<void>();

    public signpostPosition: SignpostPosition;

    public readonly signpostPositionPriorities: ConnectedPosition[] = [
        SIGNPOST_RIGHT_CONNECTED_POSITION,
        SIGNPOST_LEFT_CONNECTED_POSITION,
    ];

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Hold item's current state label.
     */
    public stateLabel: string;

    /**
     * Hold item's current state reasons.
     */
    public reasons: string[] = [];

    /**
     * Show/hide health score value.
     */
    public isHealthScoreValueAvailable: boolean;

    /**
     * Hold the health score class name based on score.
     */
    public healthScoreClass: string;

    /**
     * Show/hide deactivated icon.
     */
    public showDeactivatedIcon: boolean;

    /**
     * Show/hide exclamation indicator.
     */
    public showExclamation: boolean;

    public title: string;

    public uuid: string;

    public poolId: string | undefined;

    /**
     * Subject to control the hide/show signpost close button.
     */
    public readonly signpostCloseControl$ = new Subject<boolean>();

    /**
     * Current operational status of the item.
     */
    public operationStatus: IOperationalStatus;

    public readonly healthScoreView = HealthScoreView;

    public readonly poolObjectName = Pool.toLowerCase();

    /**
     * Current view to render based on state of health score.
     */
    public currentViewToRender = HealthScoreView.LOADING;

    public isWafPolicyAvailable = false;

    public parsedFaults: IParsedFault[] = [];

    /**
     * To hide the signpost for the first time.
     */
    private showSignpostForFirstTime = true;

    private closeTimer: ReturnType<typeof setTimeout>;

    constructor(
        public readonly l10nService: L10nService,
        public readonly schemaService: SchemaService,
        private readonly healthScoreClassPipe: HealthScoreClassPipe,
        private readonly stringService: StringService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { runTime } = changes;

        if (runTime && runTime.currentValue) {
            this.fetchHeathScoreData();
        }
    }

    /**
     * Fetch the health score related data for current item.
     */
    public handleHealthScoreClick(): void {
        this.onHealthScoreIconClick.emit();
    }

    /**
     * Fetch the health score related data for current item.
     */
    public fetchHeathScoreData(): void {
        this.currentViewToRender = HealthScoreView.LOADING;

        const { config, runtime } = this.runTime;

        this.title = config.name;
        this.uuid = config.uuid;
        this.poolId = config.poolId;
        this.objectName = this.objectName || config.type;
        this.isWafPolicyAvailable = Boolean(config?.waf_policy_ref);

        this.operationStatus = runtime.oper_status;

        this.currentViewToRender = this.operationStatus?.state === OperationalState.OPER_UP ?
            HealthScoreView.UP : HealthScoreView.DOWN;

        this.setStateLabel();
        this.setReasons();
        this.setIsHealthScoreValueAvailable();
        this.setShowExclamation();
        this.setShowDeactivatedIcon();

        if (this.showSignpostForFirstTime) {
            this.signpostCloseControl$.next(true);
            this.showSignpostForFirstTime = false;
        } else {
            this.onHealthScoreIconClick.emit();
        }

        this.setHealthScoreClass();
        this.setHealthScoreFaults();
    }

    public closeSignpost(): void {
        this.signpostCloseControl$.next(false);
    }

    /**
     * Handle click event on the view health button.
     */
    public handleViewHealthButtonClick(): void {
        this.onViewHealthClick.emit();
    }

    /**
     * Notify signpost to update position when the connected position from avi-tooltip changes.
     */
    public handleSignpostConnectedPosition(signPostConnectedPosition: ConnectedPosition): void {
        this.signpostPosition = signpostPositionMap.get(signPostConnectedPosition);
    }

    /**
     * Handle mouse enter event on the health icon.
     */
    public handleHealthScoreMouseEnter(): void {
        this.fetchHeathScoreData();
        this.cancelCloseSignpost();
    }

    /**
     * Handle mouse leave event on the health score count and tooltip.
     */
    public handleHealthScoreMouseLeave(): void {
        // We set a short timeout to allow the mouse to move into the signpost.
        this.closeTimer = setTimeout(() => {
            this.closeSignpost();
        }, 100); // 100ms delay
    }

    public cancelCloseSignpost(): void {
        // If the mouse enters the trigger or the signpost, we clear the timeout.
        if (this.closeTimer) {
            clearTimeout(this.closeTimer);
        }
    }

    /**
     * Set the heath score class name based on score.
     */
    public setHealthScoreClass(): void {
        const { state } = this.operationStatus ?? {};

        const { health_score: score } = this.runTime.health_score ?? {};

        this.healthScoreClass = this.healthScoreClassPipe.transform(score, state);
    }

    /**
     * Check if the health score is available.
     */
    public setIsHealthScoreValueAvailable(): void {
        const { state } = this.operationStatus ?? {};

        this.isHealthScoreValueAvailable = availableHealthScoreOperStates.has(state);
    }

    /**
     * Set reasons if state has reason.
     */
    public setReasons(): void {
        this.reasons = this.operationStatus?.reason ?? [];
    }

    /**
     * Set item's current state label.
     */
    public setStateLabel(): void {
        const { state } = this.operationStatus ?? {};

        if (!state) {
            this.stateLabel = this.l10nService.getMessage(globalL10nKeys.notApplicableLabel);

            return;
        }

        this.stateLabel = this.schemaService.getEnumValueLabel('OperationalState', state);
    }

    /**
     * Check whether to show the deactivated icon.
     */
    public setShowDeactivatedIcon(): void {
        const { state } = this.operationStatus ?? {};

        this.showDeactivatedIcon = deactivatedIconOperStates.has(state);
    }

    /**
     * Check whether to show the exclamation indicator.
     */
    public setShowExclamation(): void {
        const { state } = this.operationStatus ?? {};

        this.showExclamation = !noExclamationIconOperStates.has(state);
    }

    public setHealthScoreFaults(): void {
        this.parsedFaults = parseFaults(this.runTime.faults as TVirtualServiceFaults);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.closeSignpost();
    }
}
